/**
 * Where this shell's API lives, and which agent it is looking at.
 *
 * One build serves two deployments. `pa-workbench serve` puts the page at `/`
 * and there is exactly one agent behind it. A Hub puts the same page at
 * `/agents/<id>/` and routes `/agents/<id>/api/...` to that agent's own
 * workbench projection. Nothing in the document says which; the URL does, and
 * it is the only thing that does — so the prefix is read off the path and
 * prepended to every call.
 *
 * The hash is deliberately untouched. Routes are `#/today`, `#/panel/<key>`
 * and so on, and a hash rides along with whatever path it is on, so the same
 * route string works under both deployments without a rewrite.
 */

/**
 * `text` with its percent escapes decoded, or `null` when they are malformed (`%`, `%zz`).
 *
 * Every address this shell reads goes through here, so a broken link is a page that is not there rather than a
 * `URIError` thrown out of rendering.
 */
export function decodePart(text: string): string | null {
  try {
    return decodeURIComponent(text);
  } catch {
    return null;
  }
}

/** `/agents/<id>` when the page is served under one, otherwise `""`. */
export function apiBase(pathname: string): string {
  const id = agentIdFrom(pathname);
  return id === null ? "" : `/agents/${encodeURIComponent(id)}`;
}

/**
 * The agent id in a path, or `null` at the single-agent root.
 *
 * `/agents` with nothing after it is not an agent: a Hub index page has no
 * agent to prefix for, and inventing an empty id would send every call to
 * `/agents//api/...`.
 */
export function agentIdFrom(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2 || parts[0] !== "agents") return null;
  // A malformed id stays as written: the Hub answers it as no such Agent.
  const id = decodePart(parts[1]) ?? parts[1];
  return id ? id : null;
}

/** Fixed per Agent document. The Hub changes routes, but Agent API calls stay in their retained document. */
export const API_BASE = apiBase(
  typeof location === "undefined" ? "/" : location.pathname,
);

export const AGENT_ID = agentIdFrom(
  typeof location === "undefined" ? "/" : location.pathname,
);

/** `/api/...` -> the same path under this deployment's prefix. */
export function apiPath(path: string): string {
  return `${API_BASE}${path}`;
}
